import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Sparkles, CheckCircle2, Link2, Edit3, Trash2,
  Brain, Wrench, Users, FileText, FileDown, Volume2, VolumeX,
  Copy, Download
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import ChatComposer from "../components/chat/ChatComposer";
import ChatMessage from "../components/chat/ChatMessage";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import LinkToPicker from "../components/engineering/LinkToPicker";
import ShareSessionModal from "../components/engineering/ShareSessionModal";
import ShareButton from "../components/ShareButton";
import CustomExportModal from "../components/engineering/CustomExportModal";
import CreateReportModal from "../components/engineering/CreateReportModal";
import DatasheetPickerModal from "../components/engineering/DatasheetPickerModal";
import HidroMarkdown from "../components/HidroMarkdown";
import useVisualViewportHeight from "../hooks/useVisualViewportHeight";

// 2026-02-18 — Inline copy button. Falls back to a temp textarea on
// browsers / contexts where navigator.clipboard is gated (older iOS
// Safari, non-secure-origin previews). Returns void; toasts on either
// path so the user gets feedback.
async function copyToClipboard(text, label = "Copied") {
  if (!text) return;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(label);
  } catch (_e) {
    toast.error("Couldn't copy — long-press the text to select manually.");
  }
}

function MessageCopyButton({ text, testId, className = "" }) {
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={() => copyToClipboard(text, "Message copied")}
      className={`text-[10px] text-[#647087] hover:text-[#1FB6C1] transition flex items-center gap-1 ${className}`}
      title="Copy message text"
      data-testid={testId}
      aria-label="Copy message"
    >
      <Copy className="w-3 h-3" />
      Copy
    </button>
  );
}

function AgentActionLine({ a }) {
  const okClass = a.ok
    ? "text-emerald-300"
    : "text-rose-300";
  return (
    <div
      className={`text-[11px] ${okClass} flex items-start gap-1.5`}
      data-testid={`agent-action-${a.tool_call_id || a.name}`}
    >
      <span aria-hidden>{a.ok ? "✓" : "✕"}</span>
      <span className="flex-1 min-w-0">{a.summary}</span>
    </div>
  );
}

function PendingToolCall({ p, draftId, messageId, onResolved }) {
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    if (!draftId || !messageId || busy) return;
    setBusy(true);
    try {
      await api.post(
        `/workbench/drafts/${draftId}/agent/confirm`,
        { message_id: messageId, tool_call_id: p.id, action },
      );
      if (onResolved) onResolved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${action} that action.`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="mt-2 rounded border border-amber-400/40 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-100 space-y-1.5"
      data-testid={`agent-pending-${p.id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-amber-500/30 border border-amber-400/40 text-amber-50">
          Confirm
        </span>
        <span className="font-bold">{p.name}</span>
      </div>
      {p.preview && (
        <div className="text-amber-100/90 italic">{p.preview}</div>
      )}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => decide("confirm")}
          className="px-2 py-1 rounded bg-emerald-500/90 hover:bg-emerald-500 text-white text-[11px] font-bold disabled:opacity-50"
          data-testid={`agent-confirm-${p.id}`}
        >
          Confirm
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => decide("cancel")}
          className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white text-[11px] disabled:opacity-50"
          data-testid={`agent-cancel-${p.id}`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function AssistantBubble({ m, aiName = "Hidro", draftId, onAgentResolved }) {
  const txt = m.text || "";
  const actions = Array.isArray(m.agent_actions) ? m.agent_actions : [];
  const pending = Array.isArray(m.pending_tool_calls) ? m.pending_tool_calls : [];
  return (
    <div className="flex gap-3 my-3" data-testid={`eng-msg-${m.id}`}>
      <div className="shrink-0 w-8 h-8 rounded-md bg-gradient-to-br from-[#1FB6C1] to-[#0d6e75] flex items-center justify-center text-white">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold">{aiName}</span>
          {m.deep_mode && (
            <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-400/30 flex items-center gap-1">
              <Brain className="w-2.5 h-2.5" />
              Deep mode
            </span>
          )}
          {(actions.length > 0 || pending.length > 0) && (
            <span
              className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/30"
              title="Hidro executed actions on this turn"
              data-testid={`agent-badge-${m.id}`}
            >
              Agent
            </span>
          )}
          <span className="text-[10px] text-[#647087]">
            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
          {m.status !== "running" && txt && (
            <MessageCopyButton
              text={txt}
              testId={`eng-copy-assistant-${m.id}`}
              className="ml-auto"
            />
          )}
        </div>
        <HidroMarkdown
          text={txt}
          compact
          className="text-sm"
          testId={`eng-assistant-text-${m.id}`}
        />
        {actions.length > 0 && (
          <div className="mt-2 space-y-1" data-testid={`agent-actions-${m.id}`}>
            {actions.map((a) => (
              <AgentActionLine key={a.tool_call_id || `${a.name}-${a.ts}`} a={a} />
            ))}
          </div>
        )}
        {pending.length > 0 && (
          <div className="mt-1 space-y-1" data-testid={`agent-pending-list-${m.id}`}>
            {pending.map((p) => (
              <PendingToolCall
                key={p.id}
                p={p}
                draftId={draftId}
                messageId={m.id}
                onResolved={onAgentResolved}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserBubble({ m, onMediaClick }) {
  return (
    <div className="group relative">
      <ChatMessage msg={m} isMine={true} isFirstInGroup members={[]} onMediaClick={onMediaClick} />
      {m.text && (
        <div className="flex justify-end pr-1 -mt-2 mb-2 opacity-0 group-hover:opacity-100 transition">
          <MessageCopyButton
            text={m.text}
            testId={`eng-copy-user-${m.id}`}
          />
        </div>
      )}
    </div>
  );
}

export default function EngineeringSession() {
  const { sid } = useParams();
  const { user } = useAuth();
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [cats, setCats] = useState([]);
  const [deepMode, setDeepMode] = useState(true);
  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [createReportOpen, setCreateReportOpen] = useState(false);
  // Iter 231.1 — webBusy state removed alongside web-search button.
  const [dsPickerOpen, setDsPickerOpen] = useState(false);
  // Wave 25.7 — speak-replies toggle (persisted)
  const [speakOn, setSpeakOn] = useState(() => {
    try { return localStorage.getItem("eng_speak_on") === "1"; } catch (_e) { return false; }
  });
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const lastSpokenIdRef = useRef(null);
  const askingRef = useRef(false);

  const refreshMessages = async () => {
    try {
      const { data } = await api.get(`/engineering/sessions/${sid}/messages`);
      setMessages(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load messages.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get(`/engineering/sessions/${sid}`),
          api.get("/engineering/categories"),
        ]);
        setSession(s.data);
        setCats(c.data.items || []);
        setTitleDraft(s.data.title || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Session not found.");
        nav("/tools/engineering-desk");
      } finally {
        setLoading(false);
      }
      await refreshMessages();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }, [messages.length]);

  // 2026-02-18 — Background message refresh.
  // The user reported: "engineering desk doesn't reply — must leave and
  // re-enter to see the reply". Root cause was originally: pollAssistantMessage
  // only ran for messages sent in THIS browser tab. If the answer arrives
  // after a quick tab switch, the local state goes stale.
  //
  // Iter 185 (2026-05-22, Dirk) — the bug came back because the original
  // fix gated the refresh on `askingRef.current` (line below). That meant
  // while the BG worker is composing the reply, the only thing updating
  // the bubble is the dedicated single-message poll — and if THAT misses
  // a tick (transient network, tab switch, race with status flip), the
  // bubble stays empty until the user leaves and re-enters.
  // Fix: the refresh now runs UNCONDITIONALLY (the id+status+text
  // equality check on line ~210 means re-renders are still cheap).
  // Polling cadence dropped from 4s → 2s so the lag-to-reply is tighter.
  useEffect(() => {
    if (!sid) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (document.hidden) return;
      try {
        const { data } = await api.get(`/engineering/sessions/${sid}/messages`);
        if (cancelled) return;
        const fresh = data.items || [];
        setMessages((prev) => {
          // Bail out if length + last-id + last-status + last-text +
          // agent-state lengths all match — no need to re-render the
          // whole list every tick. Agent state can change without the
          // text changing (e.g. confirm → action moves from pending
          // to executed), so we hash both list sizes.
          // Iter 427.p14 — Also key on audio-transcription signature
          // so pending→done Whisper flips force a re-render even when
          // the tail message is unchanged.
          const last = prev[prev.length - 1];
          const fresh_last = fresh[fresh.length - 1];
          const prevAudioSig = prev
            .filter((m) => m.kind === "audio")
            .map((m) => `${m.id}:${m.transcription_status || "-"}`)
            .join("|");
          const freshAudioSig = fresh
            .filter((m) => m.kind === "audio")
            .map((m) => `${m.id}:${m.transcription_status || "-"}`)
            .join("|");
          if (
            prev.length === fresh.length
            && last?.id === fresh_last?.id
            && last?.status === fresh_last?.status
            && last?.text === fresh_last?.text
            && (last?.agent_actions?.length || 0) === (fresh_last?.agent_actions?.length || 0)
            && (last?.pending_tool_calls?.length || 0) === (fresh_last?.pending_tool_calls?.length || 0)
            && prevAudioSig === freshAudioSig
          ) {
            return prev;
          }
          return fresh;
        });
      } catch (_e) { /* keep polling on transient errors */ }
    };
    const handle = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [sid]);

  // Speak each new assistant reply (only the most recent, only once)
  useEffect(() => {
    if (!speakOn) return;
    const last = messages[messages.length - 1];
    if (!last || !last.is_assistant || !last.text) return;
    if (lastSpokenIdRef.current === last.id) return;
    lastSpokenIdRef.current = last.id;
    speakReply(last.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, speakOn]);

  const speakReply = async (text) => {
    if (!text) return;
    try {
      const clean = text.replace(/\[[^\]]+\]\([^)]+\)/g, "$1").replace(/[*_`#>]/g, "").slice(0, 2200);
      const voice = localStorage.getItem("hidro_voice") || "alloy";
      const resp = await api.post(
        "/hidro/voice/speak",
        { text: clean, voice },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      try { audioRef.current?.pause(); } catch (_e) { /* noop */ }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play().catch(() => { /* user-gesture required on first play */ });
      audio.onended = () => URL.revokeObjectURL(url);
    } catch (_e) {
      // Silent — user just doesn't hear it; UI still shows the text.
    }
  };

  const toggleSpeak = () => {
    const next = !speakOn;
    setSpeakOn(next);
    try { localStorage.setItem("eng_speak_on", next ? "1" : "0"); } catch (_e) { /* noop */ }
    if (!next) {
      try { audioRef.current?.pause(); } catch (_e) { /* noop */ }
    } else {
      toast.info(`${aiName} will speak its replies.`);
    }
  };

  const saveTitle = async () => {
    if (!titleDraft.trim()) { setTitleDraft(session.title); setEditTitle(false); return; }
    try {
      const { data } = await api.patch(`/engineering/sessions/${sid}`, { title: titleDraft.trim() });
      setSession(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    setEditTitle(false);
  };

  const changeCategory = async (key) => {
    try {
      const { data } = await api.patch(`/engineering/sessions/${sid}`, { category_key: key });
      setSession(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };

  // 2026-02-18 — Raw chat export. Verbatim markdown dump. No AI rewrite,
  // no summarisation. Goal: give the user a paper trail they can audit
  // ("did Hidro really say 5 L/h at 5 bar?") and archive.
  const exportRawChat = () => {
    if (!messages.length) return;
    const fmtTs = (iso) => {
      try {
        const d = new Date(iso);
        return d.toLocaleString("en-ZA", {
          year: "numeric", month: "2-digit", day: "2-digit",
          hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
      } catch (_e) { return iso || ""; }
    };
    const lines = [];
    const titleLine = session?.title || `Engineering Session ${sid}`;
    lines.push(`# ${titleLine}`);
    lines.push("");
    lines.push(`- Session ID: \`${sid}\``);
    if (session?.category_key) lines.push(`- Category: ${session.category_key}`);
    if (session?.link_kind && session?.link_label) {
      lines.push(`- Linked: ${session.link_kind} → ${session.link_label}`);
    }
    lines.push(`- Exported: ${fmtTs(new Date().toISOString())}`);
    lines.push(`- Messages: ${messages.length}`);
    lines.push("");
    lines.push("---");
    lines.push("");
    for (const m of messages) {
      const who = m.is_assistant ? aiName : (m.author_name || "User");
      const tag = m.deep_mode ? " · Deep mode" : "";
      lines.push(`## ${who} — ${fmtTs(m.created_at)}${tag}`);
      lines.push("");
      if (m.text) {
        lines.push(m.text);
        lines.push("");
      }
      if (m.upload_url) {
        const name = m.upload_filename || m.upload_url.split("/").pop() || "attachment";
        lines.push(`📎 Attachment: [${name}](${m.upload_url})`);
        if (m.upload_content_type) lines.push(`   - Type: \`${m.upload_content_type}\``);
        if (m.upload_size_bytes) lines.push(`   - Size: ${m.upload_size_bytes} bytes`);
        lines.push("");
      }
      if (m.status && m.status !== "ok" && m.status !== "done" && m.status !== "running") {
        lines.push(`> _status: ${m.status}_`);
        lines.push("");
      }
      lines.push("---");
      lines.push("");
    }
    const md = lines.join("\n");
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const safe = (titleLine || "session")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "session";
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `engineering-${safe}-${stamp}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Raw chat exported.");
  };

  // Wave 25.7 — auto-respond after every user-sent message (text, audio, file)
  // 2026-05-15 (Nuno bug #15): /ask now returns a placeholder assistant
  // message immediately with status='running'; we poll the single-message
  // endpoint until status flips. This eliminates the 60s ingress timeout
  // ghost-spinner where Hidro "thinks but doesn't deliver".
  const pollAssistantMessage = async (sentMid) => {
    let attempts = 0;
    const maxAttempts = 120; // ~ 240 seconds at 2s per poll
    while (attempts < maxAttempts) {
      await new Promise((r) => setTimeout(r, 2000));
      attempts += 1;
      try {
        const { data: m } = await api.get(`/engineering/sessions/${sid}/messages/${sentMid}`);
        if (m && m.status !== "running") {
          setMessages((prev) => prev.map((x) => (x.id === sentMid ? m : x)));
          return m;
        }
      } catch (_e) { /* keep polling */ }
    }
    setMessages((prev) =>
      prev.map((x) => (x.id === sentMid
        ? { ...x, status: "error", text: `${aiName} is taking longer than expected. Refresh the page in a minute — the answer is probably already written, just slow to surface.` }
        : x)),
    );
    return null;
  };

  const autoRespond = async (sentMsg) => {
    if (!sentMsg) return;
    if (askingRef.current) return;
    if (sentMsg.is_assistant) return;
    if (sentMsg.is_datasheet_ref) return; // datasheet inserts trigger no auto-reply
    askingRef.current = true;
    setAsking(true);
    try {
      // Returns immediately with a placeholder assistant message
      // (status='running'). Background worker fills in the text.
      const { data } = await api.post(
        `/engineering/sessions/${sid}/ask`,
        { user_message_id: sentMsg.id, deep_mode: deepMode, include_history: 30 },
        { timeout: 30_000 },
      );
      // Drop the placeholder bubble into the thread so the user sees that
      // Hidro received the question and is composing a reply.
      setMessages((prev) => [...prev, data]);
      // Poll until the worker writes a terminal status.
      await pollAssistantMessage(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${aiName} couldn't answer right now.`);
    } finally {
      askingRef.current = false;
      setAsking(false);
    }
  };

  const handleSent = (m) => {
    setMessages((prev) => [...prev, m]);
    autoRespond(m);
  };

  const doResolve = async () => {
    if (!window.confirm("Resolve this session? Hidro will generate a public write-up and add it to the Library.")) return;
    setResolving(true);
    try {
      const { data } = await api.post(`/engineering/sessions/${sid}/resolve`, {}, { timeout: 120_000 });
      setSession(data);
      toast.success("Resolved — added to Library.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve.");
    } finally {
      setResolving(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this session permanently? Cannot be undone.")) return;
    try {
      await api.delete(`/engineering/sessions/${sid}`);
      toast.success("Deleted.");
      nav("/tools/engineering-desk");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete."); }
  };

  const saveLink = async (link) => {
    try {
      const { data } = await api.patch(`/engineering/sessions/${sid}`, link);
      setSession(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link."); }
    setLinkPickerOpen(false);
  };

  const insertDatasheet = async (d) => {
    try {
      const { data } = await api.post(
        `/engineering/sessions/${sid}/insert-datasheet`,
        { datasheet_id: d.id },
      );
      setMessages((prev) => [...prev, data]);
      setDsPickerOpen(false);
      toast.success(`Inserted: ${d.title}`);
      // After inserting, run an auto-respond so Hidro can read the spec sheet
      autoRespond({ ...data, is_datasheet_ref: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not insert datasheet.");
    }
  };

  // Iter 231.1 — manual web-search button removed per user feedback.
  // Hidro's grounded answers + raw chat export cover the "force fresh data"
  // path without a dedicated button. Function body removed; state removed.

  // Iter 231.2 — for resolved sessions, the universal Share button can
  // now share the actual PDF file (via Web Share API on mobile, or a
  // silent download elsewhere) — not just a signed link. We pass an
  // async fetcher to ShareButton that renders the library report's
  // summary_public into a PDF blob on demand.
  const fetchResolvedPdf = async () => {
    const sp = session?.summary_public;
    if (!sp) return null;
    const headline = sp.headline || session.title || "Hidro report";
    const body = sp.body || sp.markdown || "";
    const resp = await api.post(
      "/engineering/render-pdf",
      { headline, body },
      { responseType: "blob", timeout: 90_000 },
    );
    const blob = new Blob([resp.data], { type: "application/pdf" });
    const filename = `${(headline || "report").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
    return { blob, filename };
  };

  // Wave 26.7 — Engineering Session is a "fullscreen-chat" surface.
  // Layout already detects this URL and applies `h-screen flex` to <main>
  // + hides the mobile bottom nav. We just need to (1) keep the keyboard's
  // visible-viewport height in sync via the same hook the main chat uses,
  // and (2) build the shell as a pure flex column (header / scroll-area /
  // composer) so the composer always pins to the keyboard line and the
  // message canvas absorbs whatever space remains. NO `calc(100vh - X)` —
  // that's exactly what caused the gap on mobile.
  useVisualViewportHeight();

  if (loading || !session) {
    return (
      <div className="flex items-center justify-center py-20 text-[#647087]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const isResolved = session.status === "resolved";
  const hasUserMessage = messages.some((m) => !m.is_assistant && m.kind === "text");
  const isOwner = session.owner_id === user?.id;
  const isReadOnly = !isOwner;

  return (
    <div
      className="flex-1 min-h-0 flex flex-col md:-mx-6 md:-mt-6 md:-mb-6 px-4 md:px-6 pt-3 md:pt-6 pb-2"
      data-testid="engineering-session"
    >
      {/* Header */}
      <div className="shrink-0 pb-3 border-b border-white/10 space-y-2">
        <div className="flex items-start gap-2">
          <button onClick={() => nav("/tools/engineering-desk")} className="hv-btn hv-btn-ghost text-xs shrink-0" data-testid="eng-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            {editTitle && isOwner ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => e.key === "Enter" && saveTitle()}
                className="hv-input text-base font-bold w-full"
                data-testid="eng-title-input"
              />
            ) : isOwner ? (
              <button onClick={() => setEditTitle(true)} className="text-left w-full group" data-testid="eng-title">
                <h1 className="font-display text-xl sm:text-2xl font-bold truncate flex items-center gap-2">
                  {session.title}
                  <Edit3 className="w-3 h-3 text-[#647087] opacity-0 group-hover:opacity-100 transition" />
                </h1>
              </button>
            ) : (
              <h1 className="font-display text-xl sm:text-2xl font-bold truncate">{session.title}</h1>
            )}
            <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px]">
              {!isResolved && isOwner ? (
                <select
                  value={session.category_key}
                  onChange={(e) => changeCategory(e.target.value)}
                  className="text-[11px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-[#cbd2df]"
                  data-testid="eng-category-select"
                >
                  {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              ) : (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[#cbd2df]">
                  {(cats.find((c) => c.key === session.category_key) || {}).label || session.category_key}
                </span>
              )}
              {isOwner ? (
                <button
                  onClick={() => setLinkPickerOpen(true)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-white/10 hover:border-[#1FB6C1]/40 text-[#cbd2df] transition"
                  data-testid="eng-link-btn"
                >
                  <Link2 className="w-3 h-3" />
                  {session.link_label ? session.link_label : "Link to record"}
                </button>
              ) : session.link_label ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-white/10 text-[#cbd2df]">
                  <Link2 className="w-3 h-3" />
                  {session.link_label}
                </span>
              ) : null}
              {!isOwner && session.owner_name && (
                <span className="text-[11px] text-[#a0aabf]">by {session.owner_name}</span>
              )}
              {isResolved && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                  <CheckCircle2 className="w-3 h-3" /> Resolved
                </span>
              )}
            </div>
          </div>
          {/* Compact icon row — replaces the old big action bar.
              Iter 231.1 — wrap on narrow screens so the export / resolve /
              delete buttons don't get clipped off the right edge on mobile. */}
          <div className="flex items-center gap-0.5 flex-wrap justify-end shrink-0">
            {/* Speaker toggle */}
            {!isResolved && isOwner && (
              <button
                onClick={toggleSpeak}
                className={`hv-btn hv-btn-ghost text-xs ${speakOn ? "text-[#1FB6C1]" : "text-[#647087]"}`}
                title={speakOn ? `${aiName} is speaking replies (click to mute)` : `Tap to have ${aiName} speak replies`}
                data-testid="eng-speak-toggle"
              >
                {speakOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
            )}
            {/* Deep mode tiny toggle */}
            {!isResolved && isOwner && (
              <button
                onClick={() => setDeepMode((v) => !v)}
                className={`hv-btn hv-btn-ghost text-xs ${deepMode ? "text-violet-300" : "text-[#647087]"}`}
                title={deepMode ? "Deep mode on — slower, more thorough" : "Deep mode off — faster, lighter"}
                data-testid="eng-deep-mode"
              >
                <Brain className="w-4 h-4" />
              </button>
            )}
            {/* Insert datasheet (mini) */}
            {!isResolved && isOwner && (
              <button
                onClick={() => setDsPickerOpen(true)}
                disabled={asking}
                className="hv-btn hv-btn-ghost text-xs text-[#647087] hover:text-[#1FB6C1]"
                title="Attach a datasheet from the library"
                data-testid="eng-insert-datasheet-btn"
              >
                <FileText className="w-4 h-4" />
              </button>
            )}
            {/* Iter 231.1 — web-search button removed per user feedback.
                Hidro's web-grounded answers + raw chat exports cover the
                "fresh data" use case without a manual button. */}

            {/* Wave 26.10.14 — universal external Share button (Copy / Chat /
                WhatsApp / Email). Distinct from team-share modal above.
                Iter 231.1 — `compact` on mobile reclaims width for the
                report / resolve buttons that were getting clipped. */}
            {isResolved ? (
              <ShareButton
                kind="library_report"
                refId={session.id}
                title={session.summary_public?.headline || session.title}
                testId="eng-session-external-share"
                compact
                onPdfShare={fetchResolvedPdf}
              />
            ) : isOwner && (
              <ShareButton
                kind="engineering_session"
                refId={session.id}
                title={session.title}
                testId="eng-session-external-share"
                compact
              />
            )}

            {isReadOnly && (
              <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-1 rounded bg-violet-500/10 text-violet-300 border border-violet-500/30">
                Read-only · shared
              </span>
            )}
            {isOwner && session.shared_with?.length > 0 && (
              <span
                className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-1 rounded bg-[#1FB6C1]/10 text-[#1FB6C1] border border-[#1FB6C1]/30"
                title={session.shared_with_meta?.map((u) => u.name).join(", ")}
                data-testid="eng-shared-count"
              >
                Shared with {session.shared_with.length}
              </span>
            )}
            {isOwner && !isResolved && (
              <button
                onClick={() => setShareOpen(true)}
                className="hv-btn hv-btn-ghost text-xs"
                title="Share read-only with another tech"
                data-testid="eng-share-btn"
              >
                <Users className="w-4 h-4" />
              </button>
            )}
            {messages.length >= 2 && (
              <button
                onClick={() => setExportOpen(true)}
                className="hv-btn hv-btn-ghost text-xs text-[#1FB6C1] hover:!text-[#1FB6C1]"
                title="Generate PDF report — pick length & audience"
                data-testid="eng-export-btn"
              >
                <FileDown className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Report</span>
              </button>
            )}
            {/* Iter 428.p5 — "Create report" that persists to Customer
                Documents, stamps the resolved family tree onto the
                doc, and (for project links) can spawn a fresh
                planning workbench. Distinct from the quick "Report"
                button above which is a throwaway export. */}
            {messages.length >= 2 && (
              <button
                onClick={() => setCreateReportOpen(true)}
                className="hv-btn hv-btn-ghost text-xs text-fuchsia-300 hover:!text-fuchsia-200"
                title="Create formal report → saved to Customer Documents"
                data-testid="eng-create-report-btn"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Create report</span>
              </button>
            )}
            {/* 2026-02-18 — Raw chat export. Downloads the full thread
                verbatim (no AI rewrite) as a markdown file the user can
                grep / paste into email / archive for audit. */}
            {messages.length >= 1 && (
              <button
                onClick={exportRawChat}
                className="hv-btn hv-btn-ghost text-xs"
                title="Download raw chat as Markdown (verbatim, full thread)"
                data-testid="eng-export-raw-btn"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            {isOwner && !isResolved && (
              <button
                onClick={doResolve}
                disabled={resolving || !hasUserMessage || messages.length < 2}
                className="hv-btn hv-btn-ghost text-xs"
                title="Resolve session → generate public write-up"
                data-testid="eng-resolve-btn"
              >
                {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Resolve
              </button>
            )}
            {isOwner && (
              <button onClick={doDelete} className="hv-btn hv-btn-ghost text-xs" title="Delete session" data-testid="eng-delete-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-1 py-3 bg-[#0a0d14]/40 rounded" data-testid="eng-messages">
        {messages.length === 0 && (
          <div className="text-center py-12 px-6">
            <div className="inline-flex w-12 h-12 rounded-xl bg-[#1FB6C1]/10 border border-[#1FB6C1]/20 items-center justify-center mb-3">
              <Wrench className="w-6 h-6 text-[#1FB6C1]" />
            </div>
            <div className="font-display text-lg font-bold mb-1">Talk to Hidro</div>
            <div className="text-xs text-[#a0aabf] max-w-md mx-auto">
              Type, dictate, paste / drop photos, attach manuals — Hidro responds live.
              Tap <Volume2 className="w-3 h-3 inline" /> in the header for spoken replies.
            </div>
          </div>
        )}
        {messages.map((m) =>
          m.is_assistant ? (
            <AssistantBubble
              key={m.id}
              m={m}
              aiName={aiName}
              myId={user?.id}
              draftId={session?.link_kind === "workbench" ? session?.link_id : null}
              onAgentResolved={async () => {
                try {
                  const { data } = await api.get(`/engineering/sessions/${sid}/messages`);
                  setMessages(data.items || []);
                } catch (_e) { /* polling will catch up */ }
              }}
            />
          ) : (
            <UserBubble key={m.id} m={m} onMediaClick={() => {}} />
          )
        )}
        {asking && (
          <div className="flex gap-3 my-3">
            <div className="shrink-0 w-8 h-8 rounded-md bg-gradient-to-br from-[#1FB6C1] to-[#0d6e75] flex items-center justify-center text-white animate-pulse">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0 pt-1.5">
              <div className="text-xs font-bold">{aiName}</div>
              <div className="text-xs text-[#a0aabf] italic">Thinking…</div>
            </div>
          </div>
        )}
      </div>

      {/* Composer — sole input. Hidro auto-replies after every send. */}
      {!isResolved && isOwner && (
        <ChatComposer
          threadId={sid}
          apiBase="/engineering/sessions"
          onSent={handleSent}
          disabled={asking}
        />
      )}

      {/* Resolved summary banner */}
      {isResolved && session.summary_public && (
        <div className="mt-3 hv-card p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] mb-1">Published to library</div>
          <h2 className="font-display text-lg font-bold mb-1">{session.summary_public.headline}</h2>
          {session.summary_public.tldr && (
            <div className="text-sm text-[#cbd2df] italic mb-3">{session.summary_public.tldr}</div>
          )}
          <HidroMarkdown text={session.summary_public.body} className="text-sm" testId="eng-summary-body" />
          {session.summary_public.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {session.summary_public.tags.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[#cbd2df]">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {linkPickerOpen && (
        <LinkToPicker
          current={{ kind: session.link_kind, id: session.link_id, label: session.link_label }}
          onClose={() => setLinkPickerOpen(false)}
          onSave={saveLink}
        />
      )}
      {shareOpen && (
        <ShareSessionModal
          session={session}
          onClose={() => setShareOpen(false)}
          onSaved={(ids) => {
            setSession((s) => ({ ...s, shared_with: ids }));
          }}
        />
      )}
      {exportOpen && (
        <CustomExportModal
          session={session}
          onClose={() => setExportOpen(false)}
        />
      )}
      {createReportOpen && (
        <CreateReportModal
          sessionId={sid}
          sessionTitle={session?.title || ""}
          onClose={() => setCreateReportOpen(false)}
          onGenerated={(res) => {
            const cust = res?.document?.customer_id;
            if (cust) {
              toast.message(
                "Saved to Customer Documents",
                { description: `Open the customer page to view the report.` },
              );
            }
            if (res?.planning_draft_id) {
              toast.success("Planning workbench opened for the project.");
            }
          }}
        />
      )}
      {dsPickerOpen && (
        <DatasheetPickerModal
          onClose={() => setDsPickerOpen(false)}
          onPick={insertDatasheet}
        />
      )}
    </div>
  );
}
